/**
 * LLM-backed parameter advisor.
 *
 * Implements the swappable `ParameterAdvisor` seam (mirroring
 * `createLlmMetricAdvisor`), but calls the real Parameter Inference API
 * (Spec 3, `POST /api/onboarding/infer-parameters`) instead of client-side
 * keyword rules. Built as a factory so the call site can bind the extra fields
 * the backend needs (toolName/category/schema/tableName/confirmed metrics)
 * that aren't part of the `ParameterSuggestionInput`.
 *
 * Reuses `buildBusinessContext` + `toColumnSamples` from `llmMetricAdvisor.ts`
 * rather than duplicating them.
 */

import {
  BaseMetricDraft,
  DerivedMetricDraft,
  InferParametersRequest,
  InferParametersResponse,
  ParamType,
  ParameterAdvisor,
  ParameterDraft,
  ParameterProposal,
  ParameterSuggestion,
  ParameterSuggestionInput,
} from './types';
import { submitInferParameters } from './onboardingApi';
import { pollInferJob } from './pollInferJob';
import { buildBusinessContext, toColumnSamples } from './llmMetricAdvisor';

const PARAM_TYPES: readonly ParamType[] = ['string', 'number', 'boolean', 'array'];

/** Dependencies the LLM parameter advisor needs beyond ParameterSuggestionInput. */
export interface LlmParameterAdvisorDeps {
  toolName: string;
  category: string;
  schema: string | null;
  tableName: string;
  confirmedBaseMetrics: BaseMetricDraft[];
  confirmedDerivedMetrics: DerivedMetricDraft[];
  supportsPeriodComparison: boolean;
  /**
   * Injectable end-to-end inference fn (submit → poll → response). Defaults to
   * the composed submitInferParameters + pollInferJob. Existing tests inject
   * this to return an InferParametersResponse directly.
   */
  inferParametersFn?: (req: InferParametersRequest) => Promise<InferParametersResponse>;
  /** Injectable submit client (Spec 3.5 async); defaults to submitInferParameters. */
  submitFn?: typeof submitInferParameters;
  /** Injectable poll loop (Spec 3.5 async); defaults to pollInferJob. */
  pollFn?: typeof pollInferJob;
}

/** Coerces the free-form LLM `type` field into a wizard ParamType (defaults to string). */
function coerceParamType(value: unknown): ParamType {
  if (typeof value === 'string' && (PARAM_TYPES as readonly string[]).includes(value)) {
    return value as ParamType;
  }
  return 'string';
}

/** Pulls enum values out of a proposal, coercing each entry to a string. */
function coerceEnumValues(value: unknown): string[] | null {
  if (Array.isArray(value)) {
    return value.map((v) => (typeof v === 'string' ? v : String(v)));
  }
  return null;
}

/** Coerces a default value to a display string (arrays/objects are JSON-stringified). */
function coerceDefault(value: unknown): string | null {
  if (value === undefined || value === null) return null;
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

/**
 * Maps the free-form inputSchema dict to ParameterDraft rows. The fields the
 * table renders (type/description/required/enum/default) are pulled out; every
 * OTHER key (items, examples, …) is stashed in `raw` so it survives the YAML
 * round-trip even though the wizard doesn't render it.
 */
function toParameterDraft(parameterName: string, spec: ParameterProposal): ParameterDraft {
  const raw: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(spec)) {
    if (!['type', 'description', 'required', 'enum', 'default'].includes(key)) {
      raw[key] = value;
    }
  }
  return {
    parameterName,
    type: coerceParamType(spec.type),
    description: typeof spec.description === 'string' ? spec.description : '',
    required: spec.required === true,
    enumValues: coerceEnumValues(spec.enum),
    defaultValue: coerceDefault(spec.default),
    raw: Object.keys(raw).length > 0 ? raw : undefined,
  };
}

/**
 * Maps the LLM response to a ParameterSuggestion. The top-level `required` list
 * is folded INTO each row's `required` boolean (the single source of truth the
 * table edits and validation reads), so a param is required if its own spec
 * said so OR it appears in the top-level `required` list. This avoids keeping a
 * second, separately-mutated list that could desync from the rows.
 */
function toSuggestion(response: InferParametersResponse): ParameterSuggestion {
  const requiredNames = new Set(response.required);
  const parameters: ParameterDraft[] = Object.entries(response.inputSchema).map(
    ([name, spec]) => {
      const draft = toParameterDraft(name, spec);
      return { ...draft, required: draft.required || requiredNames.has(name) };
    },
  );
  return {
    parameters,
    warnings: response.warnings,
  };
}

/**
 * Builds a ParameterAdvisor that proposes parameters via the Spec 3 LLM
 * endpoint. Bind it at the call site with the table/tool/metric context, then
 * pass it into ParametersStep's `advisor` prop.
 */
export function createLlmParameterAdvisor(deps: LlmParameterAdvisorDeps): ParameterAdvisor {
  const submit = deps.submitFn ?? submitInferParameters;
  const poll = deps.pollFn ?? pollInferJob;
  // Default: submit an async job, poll until COMPLETED, cast the free-form
  // result to the parameters response shape. Tests may inject `inferParametersFn`
  // to short-circuit this transport while keeping the request/response mapping.
  const callInfer =
    deps.inferParametersFn ??
    (async (req: InferParametersRequest): Promise<InferParametersResponse> => {
      const { jobId } = await submit(req);
      const result = await poll(jobId);
      return result as InferParametersResponse;
    });
  return {
    async suggest(input: ParameterSuggestionInput): Promise<ParameterSuggestion> {
      const response = await callInfer({
        toolName: deps.toolName,
        category: deps.category,
        sourceTable: {
          schema: deps.schema,
          tableName: deps.tableName,
          columns: toColumnSamples(input.columns),
        },
        confirmedMetrics: {
          baseMetrics: deps.confirmedBaseMetrics,
          derivedMetrics: deps.confirmedDerivedMetrics,
        },
        businessContext: buildBusinessContext(input.businessContext, input.columnNotes),
        supportsPeriodComparison: deps.supportsPeriodComparison,
      });
      return toSuggestion(response);
    },
  };
}
